import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Link from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENProductDescription from "@tCentro/Components/CENProductDescription";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductAddToCartButton from "@tCentro/Overrides/Components/ProductAddToCartButton";
import ProductPrice, { ProductPriceStyles } from "@tCentro/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    productSettings: getSettingsCollection(state).productSettings,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    isMinErrorObj: state.t_components.isMinError,
    isMultiErrorObj: state.t_components.isMultiError,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    priceQtyWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    quantityOrderedWithError?: TextFieldProps;
    moreOptionsStyles?: InjectableCss;
    subQTYBtn?: ButtonProps;
    addQTYBtn?: ButtonProps;
    tooltipContainerStyles?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    errorIconStyles: {
        size: 20,
        css: css`
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
            top: 30px;
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 46px;
            margin-bottom: 15px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled {
                background-color: transparent;
            }
            &:disabled:hover {
                background-color: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        left: 50px;
                        top: 2px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 105px;
            margin-bottom: 15px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
                margin-left: 5px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: #e9e9e9;
            }
            &:active {
                background-color: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        left: 122px;
                        top: 2px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -35px;
            left: -13px;
            right: 42px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            @media (max-width: 1024px) {
                top: -42px;
                right: 0px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        top: -36px;
                        left: -8px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    moreOptionsStyles: {
        css: css`
            margin-bottom: 8px;
            margin-left: 5px;
            span {
                font-family: Poppins;
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                color: ${getColor("text.link")};
            }
        `,
    },
    container: {
        gap: 10,
        css: css`
            width: 100%;
            div[class^="GridOffset"] {
                display: flex;
                flex-direction: column;
                padding: 5px 15px;
            }
        `,
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
            position: relative;
            button {
                width: auto;
            }
        `,
    },
    addToCartButton: {
        css: css`
            width: 50%;
            border-radius: 16px !important;
            border: 0 !important;
            padding: 8px 16px !important;
            min-height: 32px !important;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 8px;
                width: auto;
                flex-direction: row;
                align-items: center;
                label {
                    display: inline-flex;
                    flex: 1;
                    margin-right: 2px;
                    width: 32px;
                    align-items: center;
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                }
                div {
                    flex: 1;
                    margin-top: 0 !important;
                }
            `,
            formInputWrapper: css`
                input {
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    border: 2px solid ${COLORS.background.grayShade} !important;
                }
                input {
                    &:focus {
                        border-radius: 40px !important;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            input {
                                width: 136px !important;
                            }
                        `,
                        css`
                            input {
                                width: 136px !important;
                            }
                        `,
                        css`
                            input {
                                width: 125px !important;
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    quantityOrderedWithError: {
        cssOverrides: {
            formField: css`
                margin-right: 8px;
                width: auto;
                flex-direction: row;
                align-items: center;
                label {
                    display: inline-flex;
                    flex: 1;
                    margin-right: 2px;
                    width: 32px;
                    align-items: center;
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                }
                div {
                    flex: 1;
                    margin-top: 0 !important;
                }
            `,
            formInputWrapper: css`
                input {
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    border: 2px solid ${COLORS.alertMsgRed} !important;
                }
                input {
                    &:focus {
                        border-radius: 40px !important;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            input {
                                width: 136px !important;
                            }
                        `,
                        css`
                            input {
                                width: 136px !important;
                            }
                        `,
                        css`
                            input {
                                width: 125px !important;
                            }
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
};

const styles = productInformationStyles;

const ProductListProductInformation: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    variantSelectionCompleted,
    isMinErrorObj,
    isMultiErrorObj,
}) => {
    const { product, productInfo } = productContext;
    const [btnQty, setBtnQty] = React.useState(() => {
        if (productContext.productInfo?.qtyOrdered !== undefined && productContext.productInfo?.qtyOrdered !== null) {
            return productContext.productInfo.qtyOrdered;
        }

        if (
            productContext.product?.detail?.multipleSaleQty &&
            productContext.product?.detail?.multipleSaleQty > 0 &&
            productContext.product?.detail?.roundingRule === "Multiple Only"
        ) {
            return productContext.product.detail.multipleSaleQty;
        }
        return 0;
    });
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [listMinErr, setListMinErr] = React.useState(false);
    const [listMultiErr, setListMultiErr] = React.useState(false);
    const qtyOrdredFinalStyle =
        listMinErr || listMultiErr ? { ...styles.quantityOrderedWithError } : { ...styles.quantityOrdered };

    const addQtyFn = () => {
        setBtnQty(
            btnQty +
                (productContext.product?.detail?.multipleSaleQty &&
                productContext.product?.detail?.multipleSaleQty > 0 &&
                productContext.product?.detail?.roundingRule === "Multiple Only"
                    ? productContext.product?.detail?.multipleSaleQty
                    : 1),
        );
    };
    const subQtyFn = () => {
        const qtyValue =
            btnQty -
            (productContext.product?.detail?.multipleSaleQty &&
            productContext.product?.detail?.multipleSaleQty > 0 &&
            productContext.product?.detail?.roundingRule === "Multiple Only"
                ? productContext.product?.detail?.multipleSaleQty
                : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    const handleMouseEnter = () => {
        if (
            product?.detail?.multipleSaleQty &&
            product?.detail?.multipleSaleQty > 0 &&
            product?.detail?.roundingRule === "Multiple Only" &&
            btnQty === product?.detail?.multipleSaleQty
        ) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };
    React.useEffect(() => {
        if (productContext.productInfo?.qtyOrdered !== undefined && productContext.productInfo?.qtyOrdered !== null) {
            setBtnQty(productContext.productInfo.qtyOrdered);
        }
    }, [productContext.productInfo?.qtyOrdered]);
    let variantDisplayTrait = null;

    if (!product) {
        return null;
    }

    if (product?.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {showPrice && !product?.isVariantParent ? (
                <StyledWrapper {...styles.priceQtyWrapper}>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </StyledWrapper>
            ) : (
                <Link href={product?.canonicalUrl} {...styles.moreOptionsStyles}>
                    {translate("Configure Product")}
                </Link>
            )}
            <StyledWrapper {...styles.addToCartWrapper}>
                <Button
                    disabled={
                        !!(
                            (!variantSelectionCompleted && productContext.product?.isVariantParent) ||
                            (productContext.product?.detail?.multipleSaleQty &&
                                productContext.product?.detail?.multipleSaleQty > 0 &&
                                productContext.product?.detail?.roundingRule === "Multiple Only" &&
                                btnQty === productContext.product?.detail?.multipleSaleQty)
                        )
                    }
                    {...styles.subQTYBtn}
                    data-test-selector="subQTYBtn"
                    onClick={subQtyFn}
                    onMouseOver={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <ButtonIcon src={Minus} />
                </Button>
                {showTooltip && (
                    <StyledWrapper {...styles.tooltipContainerStyles}>
                        <Typography
                            as="span"
                            css={css`
                                color: ${getColor("info.contrast")} !important;
                            `}
                        >
                            {translate(`${product?.detail?.multipleSaleQty} unit minimum`)}
                        </Typography>
                    </StyledWrapper>
                )}
                <ProductQuantityOrdered
                    extendedStyles={qtyOrdredFinalStyle}
                    onBtnclickQtyValue={btnQty}
                    setBtnQty={setBtnQty}
                    setlMinErr={setListMinErr}
                    setlMultiErr={setListMultiErr}
                />
                <Button
                    disabled={!variantSelectionCompleted && product?.isVariantParent}
                    {...styles.addQTYBtn}
                    data-test-selector="addQTYBtn"
                    onClick={addQtyFn}
                >
                    <ButtonIcon src={Plus} />
                </Button>
                {listMultiErr === true && (
                    <StyledWrapper {...styles.errorMsgStyle}>
                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                        <Typography {...styles.errorMsgStyleTypo}>
                            {translate(
                                `This product must be ordered in multiples of ${product?.detail?.multipleSaleQty}`,
                            )}
                        </Typography>
                    </StyledWrapper>
                )}
                {listMinErr === true && (
                    <StyledWrapper {...styles.errorMsgStyle}>
                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                        <Typography {...styles.errorMsgStyleTypo}>
                            {translate(`This product has a minimum order of ${product?.detail?.multipleSaleQty} units`)}
                        </Typography>
                    </StyledWrapper>
                )}
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
